import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Radio, DollarSign, CheckCircle, XCircle, Eye, Clock, FileVideo, Image,
  AlertTriangle, ChevronRight, Play,
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const map = {
    pending: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    approved: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    rejected: 'bg-red-500/10 text-red-500 border-red-500/20',
    creator_rejected: 'bg-red-500/10 text-red-400 border-red-500/20',
    cancelled: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
  };
  const labels = { pending: 'Awaiting Review', creator_rejected: 'You Declined' };
  return <Badge className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider font-mono border ${map[status] || map.pending}`}>{labels[status] || status}</Badge>;
}

export default function CreatorBids() {
  const { token, API, user } = useAuth();
  const navigate = useNavigate();
  const [pendingBids, setPendingBids] = useState([]);
  const [allBids, setAllBids] = useState([]);
  const [activeTab, setActiveTab] = useState('review');
  const [previewAd, setPreviewAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    try {
      const [pendRes, allRes] = await Promise.all([
        axios.get(`${API}/placements/creator-pending`, { headers }),
        axios.get(`${API}/placements/incoming`, { headers }),
      ]);
      setPendingBids(pendRes.data);
      setAllBids(allRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const acceptBid = async (id) => {
    try {
      await axios.post(`${API}/placements/${id}/creator-accept`, {}, { headers });
      toast.success('Bid accepted — ad added to your queue!');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const rejectBid = async (id) => {
    try {
      await axios.post(`${API}/placements/${id}/creator-reject`, {}, { headers });
      toast.success('Bid declined.');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const fullUrl = (url) => url?.startsWith('http') ? url : `${BACKEND_URL}${url}`;

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="w-6 h-6 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button data-testid="back-to-home" variant="ghost" onClick={() => navigate('/')} className="text-gray-400 hover:text-white hover:bg-white/5 p-2"><ArrowLeft className="w-4 h-4" /></Button>
            <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-[#7C3AED]" /><span className="font-heading text-lg font-bold text-white uppercase">Incoming Bids</span></div>
          </div>
          <div className="flex items-center gap-3">
            {pendingBids.length > 0 && (
              <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 font-mono text-xs animate-pulse">
                {pendingBids.length} awaiting review
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white/5 border border-white/10 mb-6">
            <TabsTrigger data-testid="tab-review" value="review" className="data-[state=active]:bg-[#FF3B30] data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">
              Needs Review ({pendingBids.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-all-bids" value="all" className="data-[state=active]:bg-white/10 data-[state=active]:text-white font-mono text-xs uppercase tracking-wider">
              All Bids ({allBids.length})
            </TabsTrigger>
          </TabsList>

          {/* NEEDS REVIEW */}
          <TabsContent value="review">
            {pendingBids.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No bids awaiting your review</p>
                <p className="text-sm text-gray-600 mt-1">When a brand's bid is approved by the admin, it'll appear here for you to accept or decline.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pendingBids.map(bid => {
                  const isVideo = bid.adMimeType?.startsWith('video/');
                  return (
                    <div key={bid.id} data-testid={`bid-review-${bid.id}`} className="border border-yellow-500/20 rounded-lg bg-yellow-500/[0.02] p-6">
                      <div className="flex items-start gap-5">
                        {/* Ad Preview */}
                        <div className="w-32 h-20 bg-black rounded-lg overflow-hidden flex-shrink-0 border border-white/10 cursor-pointer" onClick={() => setPreviewAd(bid)}>
                          {isVideo ? (
                            <video src={fullUrl(bid.adUrl)} className="w-full h-full object-cover" muted preload="metadata" />
                          ) : (
                            <img src={fullUrl(bid.adUrl)} alt="" className="w-full h-full object-cover" />
                          )}
                        </div>

                        {/* Bid Details */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-bold text-lg">{bid.brandName}</h3>
                            {bid.belowFloor && (
                              <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 font-mono text-[9px]">
                                <AlertTriangle className="w-3 h-3 mr-1" /> Below Floor
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-400 mb-3">{bid.adFilename}</p>

                          <div className="flex items-center gap-6">
                            <div>
                              <p className="text-[10px] font-mono text-gray-500 uppercase">Their Bid</p>
                              <p className={`text-2xl font-black font-mono ${bid.belowFloor ? 'text-yellow-400' : 'text-emerald-400'}`}>${bid.bidCpm?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-mono text-gray-500 uppercase">Your Floor</p>
                              <p className="text-2xl font-black font-mono text-white">${bid.floorCpm?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-mono text-gray-500 uppercase">Slot</p>
                              <p className="text-2xl font-black font-mono text-white">{bid.slotDuration || 15}s</p>
                            </div>
                            <div>
                              <p className="text-[10px] font-mono text-gray-500 uppercase">Type</p>
                              <p className="text-sm font-mono text-gray-300 flex items-center gap-1 mt-1">{isVideo ? <><FileVideo className="w-3.5 h-3.5" /> Video</> : <><Image className="w-3.5 h-3.5" /> Image</>}</p>
                            </div>
                          </div>
                        </div>

                        {/* Accept / Reject */}
                        <div className="flex flex-col gap-2 flex-shrink-0">
                          <Button data-testid={`accept-bid-${bid.id}`} onClick={() => acceptBid(bid.id)}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm px-6">
                            <CheckCircle className="w-4 h-4 mr-2" /> Accept
                          </Button>
                          <Button data-testid={`reject-bid-${bid.id}`} variant="ghost" onClick={() => rejectBid(bid.id)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-red-500/20 text-sm px-6">
                            <XCircle className="w-4 h-4 mr-2" /> Decline
                          </Button>
                        </div>
                      </div>

                      {bid.adminNotes && (
                        <div className="mt-3 pt-3 border-t border-white/5 text-xs text-gray-500">
                          <span className="font-mono text-gray-600 uppercase">Admin note:</span> {bid.adminNotes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ALL BIDS */}
          <TabsContent value="all">
            {allBids.length === 0 ? (
              <div className="text-center py-20 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">No bids on your channel yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {allBids.map(bid => (
                  <div key={bid.id} className="border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-8 bg-black rounded overflow-hidden flex-shrink-0">
                        {bid.adMimeType?.startsWith('video/') ?
                          <video src={fullUrl(bid.adUrl)} className="w-full h-full object-cover" muted preload="metadata" /> :
                          <img src={fullUrl(bid.adUrl)} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <p className="text-sm text-white font-medium">{bid.brandName}</p>
                        <p className="text-xs text-gray-500 font-mono">{bid.adFilename} &middot; {bid.slotDuration || 15}s</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-bold text-white font-mono">${bid.bidCpm?.toFixed(2)} CPM</p>
                        <p className="text-[10px] text-gray-600 font-mono">{bid.impressions || 0} impressions &middot; ${(bid.spend || 0).toFixed(2)}</p>
                      </div>
                      <StatusBadge status={bid.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Ad Preview Dialog */}
      <Dialog open={!!previewAd} onOpenChange={() => setPreviewAd(null)}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-2xl">
          <DialogHeader><DialogTitle className="text-white font-heading">{previewAd?.adFilename}</DialogTitle><DialogDescription className="text-gray-500">From {previewAd?.brandName}</DialogDescription></DialogHeader>
          {previewAd && (
            <div className="aspect-video bg-black rounded overflow-hidden">
              {previewAd.adMimeType?.startsWith('video/') ?
                <video src={fullUrl(previewAd.adUrl)} controls autoPlay className="w-full h-full object-contain" /> :
                <img src={fullUrl(previewAd.adUrl)} alt="" className="w-full h-full object-contain" />}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
